import type { DriverShipment } from "@khazzin/api-client";
import type { OutboxEntry } from "@khazzin/api-client/sync";
import { formatTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Button, IconButton } from "@khazzin/ui/primitives/Button";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "../../i18n";
import { get, put } from "../../offline/db";
import { type Day, useCommand, useGuarded, useOffline } from "../../offline/Offline";
import { timeZone } from "./Shell";
import { inLocalOrder, isDone, isPickup, moved, windowState } from "./stops";

/**
 * Today (DA-10-15, EP10-S16): the shift, the day's progress and cash in hand,
 * then a card per stop in route order — the published route's when route
 * optimisation is on, dispatch's otherwise. A dispatch-ordered run can be
 * reordered on the phone; that is the driver's own preference, kept on the
 * phone and never sent. Everything reads the cached day.
 */

/** The driver's own order for a day, kept beside the cached run. */
interface LocalOrder {
	date: string;
	ids: string[];
}

/**
 * The day's stops in the order the driver works them: the run's, or the
 * driver's own for a dispatch-ordered run.
 */
export function useStops(): {
	stops: DriverShipment[];
	reorder: ((ids: string[]) => void) | null;
} {
	const { day } = useOffline();
	const [local, setLocal] = useState<LocalOrder | undefined>();
	useEffect(() => {
		let live = true;
		void get<LocalOrder>("run", "order").then((o) => {
			if (live) {
				setLocal(o);
			}
		});
		return () => {
			live = false;
		};
	}, []);
	const date = day?.run.date;
	const reorder = useCallback(
		(ids: string[]) => {
			if (date === undefined) {
				return;
			}
			const next = { date, ids };
			setLocal(next);
			void put("run", "order", next);
		},
		[date],
	);
	if (day === null) {
		return { stops: [], reorder: null };
	}
	const manual = day.run.source === "manual";
	const preferred = manual && local?.date === day.run.date ? local.ids : undefined;
	// `?? []`: a run with no stops came over the wire, and into the phone's
	// cache, as `stopIds: null` until 2026-10-22, and Today crashed on it.
	const ids = inLocalOrder(day.run.stopIds ?? [], preferred);
	return {
		stops: ids.flatMap((id) => day.shipments[id] ?? []),
		reorder: manual ? reorder : null,
	};
}

const WINDOW_TONES = { open: "info", closing: "warning", missed: "danger" } as const;

/** A delivery window as a chip: amber in its last half hour, red once missed. */
export function WindowChip({
	window,
	done,
	now = new Date(),
	zone = timeZone,
}: {
	window: string | null | undefined;
	done: boolean;
	now?: Date;
	/** The clock the window is read on: the phone's, which is the tenant's in the field. */
	zone?: string;
}) {
	const { t } = useI18n();
	const at = windowState(window, now, zone);
	if (at === null) {
		return null;
	}
	const tone = done ? "neutral" : WINDOW_TONES[at.state];
	const label = t(`driver.window.${at.name}`);
	return (
		<StatusChip tone={tone} size="sm">
			{done || at.state === "open" ? label : t(`driver.window.${at.state}`, { window: label })}
		</StatusChip>
	);
}

const SHIFT_COMMANDS = new Set(["shift_start", "shift_end"]);

/** The reasons a refused shift tap is explained by; anything else reads as `other`. */
const SHIFT_REASONS = new Set([
	"driver_not_active",
	"shift_already_started",
	"no_open_shift",
	"driver_not_found",
	"not_found",
	"permission_denied",
	"invalid_argument",
]);

/**
 * The newest Start or End shift tap still on the phone (ADJ-1022-4): queued,
 * not yet recorded, or refused by the server and kept for the driver to read.
 */
export function lastShiftTap(outbox: OutboxEntry[]): OutboxEntry | undefined {
	return outbox
		.filter((e) => SHIFT_COMMANDS.has(e.command))
		.reduce<OutboxEntry | undefined>((last, e) => (last && last.seq > e.seq ? last : e), undefined);
}

/** The words for a refused shift tap's reason. */
export function shiftRefusal(reason: string | undefined): string {
	return `driver.today.shiftRefused.${reason !== undefined && SHIFT_REASONS.has(reason) ? reason : "other"}`;
}

export function Today() {
	const { t, locale } = useI18n();
	const { day, online, ready, outbox, dismiss } = useOffline();
	const { stops, reorder } = useStops();
	const [reordering, setReordering] = useState(false);
	const startShift = useCommand("shift_start");
	const endShift = useCommand("shift_end");
	const [shifting, guard] = useGuarded();

	if (day === null) {
		return (
			<p className="text-[14px] text-fg-muted">
				{t(ready && !online ? "driver.today.needSignal" : "driver.today.loading")}
			</p>
		);
	}

	// A tap still queued is what the driver did, whatever the last pull said:
	// a server that asked to retry must not flip the button back (ADJ-1022-4).
	const tap = lastShiftTap(outbox);
	const queued = tap?.state === "queued" ? tap : undefined;
	const refused = tap !== undefined && tap.state !== "queued" ? tap : undefined;
	const shift = day.driver.openShift;
	const onShift = queued ? queued.command === "shift_start" : shift != null;
	const since = shift?.actualStart ?? shift?.plannedStart ?? queued?.capturedAt;
	const done = stops.filter((s) => isDone(s.status)).length;
	const wallet = day.driver.wallet;
	const ids = stops.map((s) => s.shipmentId);

	return (
		<div className="flex flex-col gap-4">
			{refused && (
				<div
					role="alert"
					className="flex items-center gap-3 rounded-md bg-danger-bg p-3 text-[13px] text-danger-fg"
				>
					<p className="me-auto">
						{t(
							refused.command === "shift_start"
								? "driver.today.startRefused"
								: "driver.today.endRefused",
						)}{" "}
						{t(shiftRefusal(refused.reason))}
					</p>
					<Button size="lg" onClick={() => void dismiss(refused.idempotencyKey)}>
						{t("driver.today.dismiss")}
					</Button>
				</div>
			)}
			<section className="flex flex-wrap items-center gap-3 card p-4">
				<p className="me-auto text-[14px] text-fg">
					{onShift && since
						? t("driver.today.onShift", { time: formatTime(since, { locale, timeZone }) })
						: t("driver.today.offShift")}
				</p>
				{/* ADJ-M03-4: the time is the tap's, not the last render's, and a
				    double tap queues one command. */}
				{/* A new tap answers a refusal: it leaves with it, so it cannot
				    resurface once the new one is recorded. */}
				{onShift ? (
					<Button
						size="lg"
						loading={shifting}
						onClick={() =>
							guard(async () => {
								if (refused) {
									await dismiss(refused.idempotencyKey);
								}
								return endShift(
									{ occurredAt: new Date().toISOString() },
									{ apply: (d) => withShift(d, null) },
								);
							})
						}
					>
						{t("driver.today.endShift")}
					</Button>
				) : (
					<Button
						variant="primary"
						size="lg"
						loading={shifting}
						onClick={() =>
							guard(async () => {
								if (refused) {
									await dismiss(refused.idempotencyKey);
								}
								const at = new Date().toISOString();
								return startShift(
									{ occurredAt: at },
									{
										apply: (d) =>
											withShift(d, {
												shiftId: "",
												driverId: d.driver.driverId,
												plannedStart: at,
												plannedEnd: null,
												actualStart: at,
												actualEnd: null,
												status: "started",
												version: 0,
											}),
									},
								);
							})
						}
					>
						{t("driver.today.startShift")}
					</Button>
				)}
				{queued && (queued.attempts > 0 || !online) && (
					<p role="status" className="w-full text-[13px] text-fg-muted">
						{t("driver.today.shiftWaiting")}
					</p>
				)}
			</section>

			{stops.length > 0 && (
				<section className="flex flex-col gap-2">
					<div className="flex items-center gap-2 text-[13px] text-fg-muted">
						<span className="me-auto">
							{t("driver.today.progress", { done, total: stops.length })}
						</span>
						{wallet && (
							<StatusChip tone="neutral" size="sm">
								{t("driver.today.cashInHand", { amount: formatMoney(wallet, locale) })}
							</StatusChip>
						)}
					</div>
					<div
						role="progressbar"
						aria-label={t("driver.today.progressLabel")}
						aria-valuemin={0}
						aria-valuemax={stops.length}
						aria-valuenow={done}
						className="h-2 overflow-hidden rounded-full bg-bg-subtle"
					>
						<div
							className="h-full rounded-full bg-brand"
							style={{ width: `${(done / stops.length) * 100}%` }}
						/>
					</div>
				</section>
			)}

			<div className="flex items-center gap-2">
				<h2 className="me-auto text-[15px] font-bold text-fg">
					{t("driver.today.stops", { count: stops.length })}
				</h2>
				{reorder && stops.length > 1 && (
					<Button size="lg" onClick={() => setReordering((r) => !r)}>
						{t(reordering ? "driver.today.reorderDone" : "driver.today.reorder")}
					</Button>
				)}
			</div>
			{stops.length === 0 ? (
				<p className="text-[14px] text-fg-muted">{t("driver.today.empty")}</p>
			) : (
				<ol className="flex flex-col gap-2">
					{stops.map((s, i) => (
						<li key={s.shipmentId} className="flex items-stretch gap-2">
							<Link
								to={`/today/${s.shipmentId}`}
								className="flex min-h-12 min-w-0 flex-1 gap-3 rounded-lg border border-border bg-bg-surface p-3"
							>
								<span className="grid size-8 shrink-0 place-items-center rounded-full bg-bg-subtle text-[13px] font-bold text-fg">
									{i + 1}
								</span>
								<StopSummary stop={s} />
							</Link>
							{reordering && reorder && (
								<div className="flex flex-col justify-center gap-1">
									<IconButton
										icon={ArrowUp}
										size="lg"
										label={t("driver.today.moveUp", { name: s.recipientName })}
										disabled={i === 0}
										onClick={() => reorder(moved(ids, s.shipmentId, -1))}
									/>
									<IconButton
										icon={ArrowDown}
										size="lg"
										label={t("driver.today.moveDown", { name: s.recipientName })}
										disabled={i === stops.length - 1}
										onClick={() => reorder(moved(ids, s.shipmentId, 1))}
									/>
								</div>
							)}
						</li>
					))}
				</ol>
			)}
		</div>
	);
}

/** A stop's card body: who, where, and the chips the driver plans by. History lists with it too. */
export function StopSummary({ stop: s }: { stop: DriverShipment }) {
	const { t, locale } = useI18n();
	const done = isDone(s.status);
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-1">
			<p className="truncate text-[14px] font-bold text-fg">{s.recipientName}</p>
			<p className="truncate text-[13px] text-fg-muted">
				{[s.cityName, s.address].filter(Boolean).join(" · ")}
			</p>
			<div className="flex flex-wrap gap-1.5">
				<StatusChip tone={done ? "success" : "neutral"} size="sm">
					{t(`driver.status.${s.status}`)}
				</StatusChip>
				<WindowChip window={s.window} done={done} />
				{isPickup(s) && (
					<StatusChip tone="brand" size="sm">
						{t("driver.pickup.badge")}
					</StatusChip>
				)}
				<StatusChip tone="neutral" size="sm">
					{t("driver.today.parcels", { count: isPickup(s) ? (s.expectedCount ?? 1) : 1 })}
				</StatusChip>
				{s.cod && s.cod.amountMinor > 0 && (
					<StatusChip tone="warning" size="sm">
						{t(s.codPaidToStore === true ? "driver.today.codForCompany" : "driver.today.cod", {
							amount: formatMoney(s.cod, locale),
						})}
					</StatusChip>
				)}
			</div>
		</div>
	);
}

/** The cached day with the driver's running shift replaced. */
function withShift(day: Day, openShift: Day["driver"]["openShift"]): Day {
	return { ...day, driver: { ...day.driver, openShift } };
}
